import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, History, Loader2, Plus, Pencil, Trash2, Link, Type, Users, ChevronDown } from 'lucide-react';
import { fetchChangelog } from '../utils/storage';
import { ChangelogAction, NodeType } from '../types';
import { nodeTone } from './nodes/nodeTheme';

/**
 * The drawer on the right of the canvas: `ns-drawer`, 360px wide, from under the top bar to the
 * bottom. Storage (in App.tsx) and the Gallery are the same drawer.
 */
const DRAWER = 'ns-drawer absolute top-[72px] right-0 h-[calc(100%-72px)] w-[360px] max-w-[90vw] z-40 overflow-hidden animate-[ns-slide-up_240ms_cubic-bezier(0.34,1.45,0.5,1)]';

interface ChangelogPanelProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

interface ChangelogEntry {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  action: ChangelogAction;
  nodeId?: string;
  nodeType?: string;
  details?: string;
  timestamp: number;
}

// The colour says what kind of change it was: mint for what was added, red for what was
// taken away, cream and ink for what was only changed.
type ChangeTone = 'ok' | 'danger' | 'ink';

const ACTION_CONFIG: Record<ChangelogAction, { icon: React.ReactNode; label: string; tone: ChangeTone }> = {
  node_added: { icon: <Plus size={12} />, label: 'Added node', tone: 'ok' },
  node_updated: { icon: <Pencil size={12} />, label: 'Updated node', tone: 'ink' },
  node_deleted: { icon: <Trash2 size={12} />, label: 'Deleted node', tone: 'danger' },
  connection_added: { icon: <Link size={12} />, label: 'Added connection', tone: 'ok' },
  connection_deleted: { icon: <Link size={12} />, label: 'Removed connection', tone: 'danger' },
  project_renamed: { icon: <Type size={12} />, label: 'Renamed workspace', tone: 'ink' }, // (what the app calls it: 'project' is the old name in the data)
  user_shared: { icon: <Users size={12} />, label: 'Shared workspace', tone: 'ok' },
  user_unshared: { icon: <Users size={12} />, label: 'Revoked access', tone: 'danger' },
  permission_changed: { icon: <Users size={12} />, label: 'Changed permission', tone: 'ink' },
};

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function groupByDate(entries: ChangelogEntry[]): Map<string, ChangelogEntry[]> {
  const groups = new Map<string, ChangelogEntry[]>();
  for (const entry of entries) {
    const date = new Date(entry.timestamp).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
    const group = groups.get(date) || [];
    group.push(entry);
    groups.set(date, group);
  }
  return groups;
}

export const ChangelogPanel: React.FC<ChangelogPanelProps> = ({
  isOpen,
  onClose,
  projectId,
}) => {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<number | undefined>();
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadEntries = useCallback(async (before?: number) => {
    setIsLoading(true);
    try {
      const result = await fetchChangelog(projectId, { limit: 30, before });
      if (before) {
        setEntries(prev => [...prev, ...result.entries]);
      } else {
        setEntries(result.entries);
      }
      setHasMore(result.hasMore);
      setNextCursor(result.nextCursor);
    } catch {
      // Silent fail
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen && projectId) {
      loadEntries();
    }
  }, [isOpen, projectId, loadEntries]);

  const loadMore = () => {
    if (hasMore && nextCursor && !isLoading) {
      loadEntries(nextCursor);
    }
  };

  if (!isOpen) return null;

  const grouped = groupByDate(entries);

  return (
    // The drawer of this side of the canvas, the same one as Storage and the Gallery: 360px of
    // glass under the top bar. (While it is open the app moves the zoom controls and the map left of it.)
    <div className={DRAWER} data-canvas-drawer="changes">
      <div className="ns-drawer-head">
        <h2 className="ns-t-heading flex items-center gap-2">
          <History size={16} className="text-ink-soft" />
          Change history
        </h2>
        <button onClick={onClose} className="ns-icon-btn" title="Close" aria-label="Close">
          <X />
        </button>
      </div>

      {/* Entries */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
        {entries.length === 0 && !isLoading && (
          <div className="ns-empty py-10">
            <span className="ns-mark ns-mark-lg ns-mark-lavender"><History /></span>
            <p className="ns-empty-title">No changes yet</p>
            <p className="ns-empty-text">Edits to this workspace, by you and by the people it is shared with, are listed here.</p>
          </div>
        )}

        {Array.from(grouped.entries()).map(([date, dateEntries]) => (
          <div key={date} className="mb-4">
            <p className="ns-t-label mb-2">{date}</p>
            <div className="space-y-1">
              {dateEntries.map(entry => {
                const config = ACTION_CONFIG[entry.action] || {
                  icon: <Pencil size={12} />,
                  label: entry.action,
                  tone: 'ink' as ChangeTone,
                };
                return (
                  <div key={entry.id} className="flex items-start gap-2.5 py-1.5">
                    <span data-tone={config.tone} className="ns-mark [--size:24px]">
                      {config.icon}
                    </span>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="ns-t-small">
                        <span className="font-semibold text-ink">{entry.userName || 'Someone'}</span>
                        {' '}{config.label.toLowerCase()}
                        {entry.details && (
                          <span className="text-ink-faint">
                            {' — '}
                            {/* A change to a node carries the colour of that kind of node, as its header does on the canvas */}
                            {entry.nodeType && <span data-tone={nodeTone(entry.nodeType as NodeType)} className="ns-tone-dot inline-block mr-1" aria-hidden="true" />}
                            {entry.details}
                          </span>
                        )}
                      </p>
                      <p className="ns-t-meta mt-0.5">{formatRelativeTime(entry.timestamp)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-center py-4">
            <Loader2 size={16} className="animate-spin text-ink-faint" />
          </div>
        )}

        {hasMore && !isLoading && (
          <button
            onClick={loadMore}
            className="ns-btn ns-btn-soft ns-btn-xs ns-btn-flat w-full mt-1"
          >
            <ChevronDown size={14} />
            Load more
          </button>
        )}
      </div>
    </div>
  );
};
